import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { stopwatchPause } from "~/store/actions/actions.stopwatches";
import { getStopwatchOffset } from "~/store/selectors/selector.additionalObjectData";
import { getActiveStopwatch } from "~/store/selectors/selector.stopwatches";
import { calculateElapsed, formatTime, stopwatchMaximum } from "~/utils/date";
import { sendStopwatchElectronMessage } from "~/utils/utils";
import { StopwatchContextProvider } from "./context";
import { usePageVisibility } from "../Hooks/usePageVisibility";

export const StopwatchProvider = ({ children }) => {
  const dispatch = useDispatch();
  const offset = useSelector(getStopwatchOffset);
  const activeStopwatch = useSelector(getActiveStopwatch);
  const timer = useRef(null);
  const initialElapsedTimeRef = useRef();
  const [elapsed, setElapsed] = useState(0);
  const [id, setId] = useState(0);
  const isVisible = usePageVisibility();

  if (activeStopwatch?.id) {
    const elapsedCalculated = calculateElapsed(activeStopwatch?.started_on, activeStopwatch?.elapsed, offset);
    initialElapsedTimeRef.current = elapsedCalculated;
  }

  useEffect(() => {
    if (isVisible) {
      setElapsed(initialElapsedTimeRef.current);
    }
    if (activeStopwatch?.started_on) {
      setId(activeStopwatch.id);
      sendStopwatchElectronMessage(calculateElapsed(activeStopwatch.started_on, activeStopwatch.elapsed, offset));
      const tick = () => {
        const nextValue = calculateElapsed(activeStopwatch.started_on, activeStopwatch.elapsed, offset);
        if (nextValue % 60 === 0) {
          setElapsed(nextValue);
          sendStopwatchElectronMessage(nextValue);
        }
        if (nextValue >= stopwatchMaximum) {
          dispatch(stopwatchPause(activeStopwatch.id, activeStopwatch.started_on, activeStopwatch.elapsed, nextValue));
        }
      };
      if (timer.current) {
        clearInterval(timer.current);
      }
      tick();
      timer.current = setInterval(tick, 1000);
    }

    if (!activeStopwatch?.started_on && timer.current) {
      clearInterval(timer.current);
    }

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
      }
    };
  }, [activeStopwatch?.elapsed, activeStopwatch?.id, activeStopwatch?.started_on, dispatch, isVisible, offset]);

  const [hours, minutes] = formatTime(elapsed);
  return (
    <StopwatchContextProvider
      value={{
        hours,
        minutes,
        id,
      }}
    >
      {children}
    </StopwatchContextProvider>
  );
};

StopwatchProvider.propTypes = {
  children: PropTypes.any,
};
